import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { ImgWebp } from '../../commons/ImgWeb';
import freeBrizTvWebp from '../../../assets/images/cartoons/FreeBrizTv.webp';
import freeBrizTvPng from '../../../assets/images/cartoons/FreeBrizTv.png';
import { FormInputText } from '../../inputs/FormInputText';
import { ButtonClassNames, InputRegexPattern, InputTextFormat } from '../../../models/Common';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { FormInputPassword } from '../../inputs/FormInputPassword';

interface FreeBrizTvProps {
  handleConnectBrizTv: ({ password, email, phone }: { password: string; email: string; phone: string }) => void;
  isLoadingConnectBrizTv: boolean;
  isBrizTvExist: boolean;
  handleActivateShareOneClick: () => void;
  isRegistrationActivated: boolean;
}

interface FreeBrizTvForm {
  email: string;
  phone: string;
  password: string;
  passwordConfirmation: string;
}

export const ForIPTV: FC<FreeBrizTvProps> = ({
  handleConnectBrizTv,
  isLoadingConnectBrizTv,
  isBrizTvExist,
  handleActivateShareOneClick,
  isRegistrationActivated,
}) => {
  const { t } = useTranslation();
  const methods = useForm<FreeBrizTvForm>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const { handleSubmit, watch, register } = methods;

  const password = useRef({});
  password.current = watch('password', '');

  const passwordConfirmRule = {
    validate: (value: string) => value === password.current || t('validation.passwordsDoNotMatch').toString(),
  };

  return (
    <>
      <div className="header promo-briz-tv__header">
        <ImgWebp webpSrc={freeBrizTvWebp} fallback={freeBrizTvPng} alt="tv" className="promo-briz-tv__header__img" />
      </div>
      <div className="content promo-briz-tv promo-briz-tv__content">
        <h1 className="title footnote_after">{t('connectionBrizTv.tryBrizTvPremiumForFree')}</h1>
        <div className={`promo-briz-tv__content__maine-container ${isBrizTvExist && 'connected'}`}>
          <div className="promo-briz-tv__content__maine-container__description">
            <p className="promo-briz-tv__content__maine-container__description__connect-action footnote_after">
              {t('connectionBrizTv.subscribeToPromotion')}
            </p>
            <p>{t('connectionBrizTv.moviesSeriesCartoonsOnYourGadgets')}</p>
            <p>{t('connectionBrizTv.watchOnTvComputerTabletSmartphone')}</p>
          </div>
          <div className="promo-briz-tv__content__maine-container__form-container">
            {isBrizTvExist && !isRegistrationActivated ? (
              <div className="promo-briz-tv__content__maine-container__form-container__form">
                <p className="promo-briz-tv__content__maine-container__form-container__form__rules-agreement">
                  {t('connectionBrizTv.byRegisteringIAgreeToRulesOf')}
                  <a href={process.env.REACT_APP_BRIZ_TV_PREMIUM_RULES || ''} target="_blank" rel="noreferrer">
                    {t('connectionBrizTv.rulesOfPromotion')}
                  </a>
                </p>
                <div className="promo-briz-tv__content__maine-container__form-container__form__btn-group">
                  <CustomMUIButton
                    label={t('buttons.register')}
                    className={ButtonClassNames.PrimaryFulfilled}
                    handleClick={handleActivateShareOneClick}
                    isBold
                    isLoading={isLoadingConnectBrizTv}
                  />
                  <p className="promo-briz-tv__content__maine-container__form-container__form__btn-group__footnote footnote_before">
                    {t('connectionBrizTv.freeOfChargeMeansPaymentForServiceForOneUah')}
                  </p>
                </div>
              </div>
            ) : (
              <FormProvider {...methods}>
                <form
                  className="promo-briz-tv__content__maine-container__form-container__form"
                  onSubmit={handleSubmit(handleConnectBrizTv)}
                >
                  <FormInputText
                    name="phone"
                    label={t('input.phone')}
                    className="primary"
                    format={InputTextFormat.Phone}
                    pattern={InputRegexPattern.Phone}
                    minLength={9}
                    deps={['email']}
                    validate={(v) => !!v || !!watch('email') || t('validation.phoneOrEmailRequired').toString()}
                  />
                  <FormInputText
                    name="email"
                    label={t('input.email')}
                    className="primary"
                    autoComplete="new-password"
                    pattern={InputRegexPattern.Email}
                    deps={['phone']}
                    validate={(v) => !!v || !!watch('phone') || t('validation.phoneOrEmailRequired').toString()}
                  />
                  <FormInputPassword
                    name="password"
                    label={t('auth.password')}
                    className="primary"
                    ref={(e) => register('password', { value: e?.value || '' })}
                    isRequired
                    helperText={t('input.numberOfCharactersInLatinNumbersOneNumberLetterRequired', {
                      from: 6,
                      to: 16,
                    })}
                    autoComplete="new-password"
                    pattern={InputRegexPattern.Any}
                    minLength={6}
                    maxLength={16}
                  />
                  <FormInputPassword
                    name="passwordConfirmation"
                    label={t('connectionBrizTv.repeatPassword')}
                    ref={() => register('passwordConfirmation', passwordConfirmRule)}
                    className="primary"
                    pattern={InputRegexPattern.Any}
                    isRequired
                  />
                  <p className="promo-briz-tv__content__maine-container__form-container__form__rules-agreement">
                    {t('connectionBrizTv.byRegisteringIAgreeToRulesOf')}
                    <a href={process.env.REACT_APP_BRIZ_TV_PREMIUM_RULES || ''} target="_blank" rel="noreferrer">
                      {t('connectionBrizTv.rulesOfPromotion')}
                    </a>
                  </p>
                  <div className="promo-briz-tv__content__maine-container__form-container__form__btn-group">
                    <CustomMUIButton
                      label={t('buttons.register')}
                      type="submit"
                      className={ButtonClassNames.PrimaryFulfilled}
                      isBold
                      isLoading={isLoadingConnectBrizTv}
                    />
                    <p className="promo-briz-tv__content__maine-container__form-container__form__btn-group__footnote footnote_before">
                      {t('connectionBrizTv.freeOfChargeMeansPaymentForServiceForOneUah')}
                    </p>
                  </div>
                </form>
              </FormProvider>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
